export const DELAY_BAND_ON_TIME = "on_time";
export const DELAY_BAND_YELLOW = "yellow";
export const DELAY_BAND_AMBER = "amber";
export const DELAY_BAND_RED = "red";
export const DELAY_BAND_NEUTRAL = "neutral";

export type DelayBandId =
  | typeof DELAY_BAND_ON_TIME
  | typeof DELAY_BAND_YELLOW
  | typeof DELAY_BAND_AMBER
  | typeof DELAY_BAND_RED
  | typeof DELAY_BAND_NEUTRAL;

export const DELAY_BAND_LIMITS = {
  onTimeMaxMinutes: 15,
  yellowMaxMinutes: 120,
  amberMaxMinutes: 180,
} as const;

export const delayMinutesFromTimestamps = (
  scheduled: string | Date | null | undefined,
  actual: string | Date | null | undefined,
) => {
  if (!scheduled || !actual) {
    return null;
  }
  const start = scheduled instanceof Date ? scheduled : new Date(scheduled);
  const end = actual instanceof Date ? actual : new Date(actual);
  if (Number.isNaN(start.valueOf()) || Number.isNaN(end.valueOf())) {
    return null;
  }
  return (end.getTime() - start.getTime()) / 60_000;
};

export const getDelayBand = (minutes: number | null | undefined): DelayBandId => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) {
    return DELAY_BAND_NEUTRAL;
  }
  const value = Number(minutes);
  if (!Number.isFinite(value)) {
    return DELAY_BAND_NEUTRAL;
  }
  if (value <= DELAY_BAND_LIMITS.onTimeMaxMinutes) {
    return DELAY_BAND_ON_TIME;
  }
  if (value <= DELAY_BAND_LIMITS.yellowMaxMinutes) {
    return DELAY_BAND_YELLOW;
  }
  if (value <= DELAY_BAND_LIMITS.amberMaxMinutes) {
    return DELAY_BAND_AMBER;
  }
  return DELAY_BAND_RED;
};

export const delayBandCssVar = (band: DelayBandId) => {
  switch (band) {
    case DELAY_BAND_ON_TIME:
      return { color: "var(--green)", background: "var(--green-bg)" };
    case DELAY_BAND_YELLOW:
      return { color: "var(--yellow)", background: "var(--yellow-bg)" };
    case DELAY_BAND_AMBER:
      return { color: "var(--amber)", background: "var(--amber-bg)" };
    case DELAY_BAND_RED:
      return { color: "var(--red)", background: "var(--red-bg)" };
    default:
      return { color: "var(--muted)", background: "var(--panel2)" };
  }
};

export const delayBandLabel = (band: DelayBandId) => {
  switch (band) {
    case DELAY_BAND_ON_TIME:
      return "On time";
    case DELAY_BAND_YELLOW:
      return "Short delay";
    case DELAY_BAND_AMBER:
      return "Medium delay";
    case DELAY_BAND_RED:
      return "Long delay";
    default:
      return "Unknown delay";
  }
};

export const isDelayedBand = (band: DelayBandId) =>
  band === DELAY_BAND_YELLOW || band === DELAY_BAND_AMBER || band === DELAY_BAND_RED;

export const delayBandDeltaTone = (
  band: DelayBandId,
): "neutral" | "success" | "warning" | "error" => {
  if (band === DELAY_BAND_ON_TIME) {
    return "success";
  }
  if (band === DELAY_BAND_YELLOW || band === DELAY_BAND_AMBER) {
    return "warning";
  }
  if (band === DELAY_BAND_RED) {
    return "error";
  }
  return "neutral";
};

export const worstDelayBand = (bands: DelayBandId[]) => {
  const rank: Record<DelayBandId, number> = {
    [DELAY_BAND_NEUTRAL]: 0,
    [DELAY_BAND_ON_TIME]: 1,
    [DELAY_BAND_YELLOW]: 2,
    [DELAY_BAND_AMBER]: 3,
    [DELAY_BAND_RED]: 4,
  };
  return bands.reduce<DelayBandId>((worst, band) => (
    rank[band] > rank[worst] ? band : worst
  ), DELAY_BAND_NEUTRAL);
};
